import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Radio, Spin, Tree } from "antd";
// FLAT BUFFERS
import { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
// UTILS
import { PermissionMode, applyCheckedKeys, roleMenuTreeData } from "../utils/roleMenu";

/**
 * Inline permission editor for the wizard's Users & Roles step.
 *
 * Renders the role menu (pages → sub-pages → actions) as a checkable
 * tree with an Online / Offline mode switch — the same two flags the
 * dedicated Roles & Permissions page edits, but without leaving the
 * wizard. The menu is modeled on the generated FlatBuffer Object API
 * (RoleMenuT) end to end; JSON only appears at the REST boundary via
 * roleMenuTToMenuJson in the caller.
 *
 * Checking an action enables it for the selected mode; page and
 * sub-page flags are derived from their descendants by
 * applyCheckedKeys, so the tree never produces a menu shape the
 * backend validator rejects.
 */
function WizardPermissionTreeEditor({
  menu,
  onChange,
  loading = false,
}: {
  menu: RoleMenuT | null;
  onChange: (next: RoleMenuT) => void;
  loading?: boolean;
}) {
  const [mode, setMode] = useState<PermissionMode>("online");

  const { treeData, checkedKeys } = useMemo(() => {
    if (!menu) return { treeData: [], checkedKeys: [] };
    return roleMenuTreeData(menu, mode);
  }, [menu, mode]);

  // Expansion is CONTROLLED so swapping the permission source (e.g. a
  // different base role) re-expands the fresh tree — AntD's
  // defaultExpandedKeys is read once on mount and would leave the new
  // tree collapsed. Re-seeding keys off the top-level key signature
  // (stable across checkbox edits, changed on real source swaps) keeps
  // the operator's manual expand/collapse intact while editing.
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const topLevelKeySignature = useMemo(
    () => treeData.map((node) => node.key).join("|"),
    [treeData],
  );
  useEffect(() => {
    setExpandedKeys(topLevelKeySignature === "" ? [] : topLevelKeySignature.split("|"));
  }, [topLevelKeySignature]);

  const onCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
    if (!menu) return;
    const keys = Array.isArray(checked) ? checked : checked.checked;
    onChange(applyCheckedKeys(menu, keys.map(String), mode));
  };

  if (loading) {
    return (
      <div className="generalStyles__flexCenter" style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!menu) {
    return <Alert type="info" showIcon message="Select a permission source to edit its menu." />;
  }

  return (
    <div>
      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value as PermissionMode)}
        style={{ marginBottom: 8 }}
        size="small"
      >
        <Radio.Button value="online">Online permissions</Radio.Button>
        <Radio.Button value="offline">Offline permissions</Radio.Button>
      </Radio.Group>
      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid rgba(128, 128, 128, 0.25)",
          borderRadius: 4,
          padding: 8,
        }}
      >
        <Tree
          checkable
          selectable={false}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys)}
          treeData={treeData}
          checkedKeys={checkedKeys}
          onCheck={onCheck}
        />
      </div>
    </div>
  );
}

export default WizardPermissionTreeEditor;
